import { Alert, Button, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import ProductDetailMapDialog from "./ProductDetailMapDialog";
import type { ConsignmentRow } from "./types";

export interface ConsignmentDetailPageProps {
    auditConsignment?: (id: number | string, payload: { state: boolean; auditDescn: string }) => Promise<void>;
    closeConsignment?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfConsignment?: (id: number | string) => Promise<void>;
    loadConsignment?: (id: number | string) => Promise<ConsignmentRow>;
    permissions?: string[];
}

/** 复用 Vue 寄售商品详情接口。 */
export async function loadConsignmentDetail(id: number | string): Promise<ConsignmentRow> {
    const response = await request.service.get<ConsignmentRow>(`/admin/product-consignment/vo/${id}`);
    return response.data;
}

/** 复用 Vue 寄售商品审核接口。 */
export async function auditConsignment(
    id: number | string,
    payload: { state: boolean; auditDescn: string }
): Promise<void> {
    await request.service.put(`/admin/product-consignment/audit/${id}`, payload);
}

/** 复用 Vue 寄售商品下架接口。 */
export async function downShelfConsignment(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-consignment/down-shelf/${id}`);
}

/** 复用 Vue 寄售商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeConsignment(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-consignment/close/${id}`, { unActiveFileIds });
}

/** 将 Vue 寄售详情的交易枚举统一为 React 详情分支使用的数字。 */
function normalizeConsignmentDetail(row: ConsignmentRow): ConsignmentRow {
    return {
        ...row,
        productConsignment: {
            ...row.productConsignment,
            priceType: row.productConsignment.priceType == null ? undefined : Number(row.productConsignment.priceType),
            isIncludeTax:
                row.productConsignment.isIncludeTax == null ? undefined : Number(row.productConsignment.isIncludeTax),
            taxRate: row.productConsignment.taxRate == null ? undefined : Number(row.productConsignment.taxRate),
            waitingPayTime:
                row.productConsignment.waitingPayTime == null
                    ? undefined
                    : Number(row.productConsignment.waitingPayTime),
            state: row.productConsignment.state == null ? undefined : Number(row.productConsignment.state),
            publishState:
                row.productConsignment.publishState == null ? undefined : Number(row.productConsignment.publishState)
        }
    };
}

const paymentTypeLabels: Record<number, string> = { 1: "线上支付", 2: "线下支付", 3: "线上支付、线下支付" };

/** 将旧 Vue 数值字段保持为带单位的详情文本。 */
function formatWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "-" : `${value}${unit}`;
}

/** 保持旧 Vue 的提货周期展示，兼容接口只返回一端日期的情况。 */
function formatDeliveryPeriod(start?: string, end?: string): string {
    if (start && end) return `${start} 至 ${end}`;
    return start || end || "-";
}

/** 将旧 Vue 参数列表转换成 BaseItem 可直接渲染的文本。 */
function formatParameterInfo(parameterInfo?: ConsignmentRow["productConsignment"]["parameterInfo"]): string {
    return parameterInfo?.length
        ? parameterInfo.map(item => `${item.parameterName || "-"} ${item.parameterValue || "-"}`).join("、")
        : "-";
}

/** 将旧 Vue 标签数组转换成详情文本。 */
function formatTags(tags?: string[]): string {
    return tags?.length ? tags.join("、") : "-";
}

/** 将旧 Vue 品类及产区简称保持为列表相同的展示格式。 */
function formatProductType(productType?: ConsignmentRow["productType"]): string {
    return productType?.name
        ? `${productType.name}${productType.areaAbbreviation ? ` (${productType.areaAbbreviation})` : ""}`
        : "-";
}

/** 寄售商品详情页，展示 Vue 详情页中的交易、提货、结算和服务字段。 */
export function ConsignmentDetailPage({
    auditConsignment: submitAudit = auditConsignment,
    closeConsignment: close = closeConsignment,
    downShelfConsignment: removeFromShelf = downShelfConsignment,
    loadConsignment = loadConsignmentDetail,
    permissions
}: ConsignmentDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const id = new URLSearchParams(location.search).get("id");
    const isAuditPage = location.pathname.endsWith("/audit");
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [detail, setDetail] = useState<ConsignmentRow>();
    const [product, setProduct] = useState<ConsignmentRow["productConsignment"]>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<boolean | null>(null);
    const [auditDescn, setAuditDescn] = useState("");
    const [auditSubmitting, setAuditSubmitting] = useState(false);

    useEffect(() => {
        if (!id) return;
        void loadConsignment(id)
            .then(result => {
                const normalizedResult = normalizeConsignmentDetail(result);
                setErrorMessage(null);
                setDetail(normalizedResult);
                setProduct(normalizedResult.productConsignment);
            })
            .catch(() => setErrorMessage("寄售商品详情加载失败"));
    }, [id, loadConsignment]);

    const refreshDetail = async () => {
        if (!id) return;
        const normalizedResult = normalizeConsignmentDetail(await loadConsignment(id));
        setDetail(normalizedResult);
        setProduct(normalizedResult.productConsignment);
    };

    const handleAudit = async () => {
        if (!id || auditState == null) return;
        if (!auditState && !auditDescn.trim()) {
            setErrorMessage("请填写驳回原因");
            return;
        }
        setAuditSubmitting(true);
        try {
            await submitAudit(id, { state: auditState, auditDescn: auditDescn.trim() });
            navigate("/productManagement/consignment");
        } catch {
            setErrorMessage("寄售商品审核失败");
        } finally {
            setAuditSubmitting(false);
        }
    };

    const handleClose = async () => {
        if (!id || !detail) return;
        const unActiveFileIds = [
            detail.mainFile?.id,
            detail.videoFile?.id,
            ...(detail.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            navigate("/productManagement/consignment");
        } catch {
            // 关闭接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("寄售商品关闭失败");
        }
    };

    const handleDownShelf = async () => {
        if (!id) return;
        try {
            await removeFromShelf(id);
            await refreshDetail();
        } catch {
            // 下架或刷新接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("寄售商品下架失败");
        }
    };

    return (
        <section aria-label="consignment-detail-page">
            <>
                <ZhDetailHeader
                    leftInfos={[{ label: "商品编号", value: product?.id || "-" }]}
                    rightInfos={[
                        {
                            label: "商品专员",
                            value: `${product?.handlerName || "-"} | ${product?.handlerMobile?.join(",") || "-"}`
                        }
                    ]}
                    stateDesc={`${product?.publishState === 4 ? "驳回" : product?.state === 1 ? "上架" : "下架"} | ${product?.publishState === 5 ? "已关闭" : "未关闭"}`}
                    title={isAuditPage ? "审核寄售商品" : "寄售商品详情"}
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {product ? (
                    <ZhMultiGrid>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhGridItem span={4}>
                                {/* beta.13 BaseItem 会额外输出无障碍镜像，标题保留单一文本节点以匹配旧 Vue 查询语义。 */}
                                <div>商品标题: {product.title || "-"}</div>
                            </ZhGridItem>
                            <ZhBaseItem label="权重值" value={product.weight ?? "-"} />
                            <ZhBaseItem label="销售单价" value={formatWithUnit(product.unitPrice, "元/吨")} />
                            <ZhBaseItem
                                label="单价类型"
                                value={
                                    product.priceType === 1 ? "固定价格" : product.priceType === 2 ? "预估价格" : "-"
                                }
                            />
                            <ZhBaseItem
                                label="是否含税"
                                value={product.isIncludeTax === 1 ? "含税" : product.isIncludeTax === 0 ? "不含" : "-"}
                            />
                            <ZhBaseItem
                                label="税率"
                                value={
                                    product.taxRate
                                        ? `${product.taxRate === 1 ? 3 : product.taxRate === 2 ? 6 : product.taxRate === 3 ? 9 : 13}%`
                                        : "-"
                                }
                            />
                            <ZhBaseItem
                                label="等待付款时间"
                                value={
                                    product.waitingPayTime
                                        ? ["3小时", "12小时", "24小时", "48小时", "72小时", "5天", "7天", "不限时长"][
                                              product.waitingPayTime - 1
                                          ]
                                        : "-"
                                }
                            />
                            <ZhBaseItem label="起订吨数" value={formatWithUnit(product.minOrderTon, "吨")} />
                            <ZhBaseItem label="库存量" value={formatWithUnit(product.inventoryQuantity, "吨")} />
                            <ZhBaseItem label="最小调整吨数" value={formatWithUnit(product.minAdjustmentTon, "吨")} />
                            <ZhBaseItem label="主图文件 ID" value={product.imgMainId ?? "-"} />
                            <ZhBaseItem label="视频文件 ID" value={product.videoId ?? "-"} />
                            <ZhBaseItem label="供应商 ID" value={product.supplierId ?? "-"} />
                            <ZhBaseItem label="品类 ID" value={product.productTypeId || "-"} />
                            <ZhBaseItem
                                label="供应商"
                                value={
                                    detail?.productConsignment.supplierEnterprise?.enterpriseName ||
                                    detail?.productConsignment.supplierEnterprise?.supplierName ||
                                    "-"
                                }
                            />
                            <ZhBaseItem label="品类" value={formatProductType(detail?.productType)} />
                            <ZhBaseItem
                                label="品类标签"
                                value={formatTags(
                                    detail?.productTypeVo?.tags?.map(tag => tag.name).filter(Boolean) as string[]
                                )}
                            />
                            <ZhBaseItem label="参数值" span={4} value={formatParameterInfo(product.parameterInfo)} />
                            <ZhBaseItem label="商品标签" span={4} value={formatTags(product.productLabel)} />
                            <ZhBaseItem label="采区" value={product.area || "-"} />
                            <ZhBaseItem
                                label="区域编码"
                                value={
                                    [product.provinceCode, product.cityCode, product.regionCode]
                                        .filter(Boolean)
                                        .join("/") || "-"
                                }
                            />
                            <ZhBaseItem label="提货地址" value={product.address || "-"} />
                            <ProductDetailMapDialog location={product} />
                            <ZhBaseItem
                                label="经纬度"
                                value={product.geo ? `${product.geo.lng ?? "-"},${product.geo.lat ?? "-"}` : "-"}
                            />
                            <ZhBaseItem
                                label="提货周期"
                                value={formatDeliveryPeriod(product.deliveryStartTime, product.deliveryEndTime)}
                            />
                            <ZhBaseItem
                                label="下单要求"
                                value={
                                    product.settlementMethod === 1
                                        ? "全部货款"
                                        : product.settlementMethod === 2
                                          ? "支付定金"
                                          : "-"
                                }
                            />
                            <ZhBaseItem label="支付方式" value={paymentTypeLabels[product.payType || 0] || "-"} />
                            <ZhBaseItem label="提货说明" span={4} value={product.deliveryDescn || "-"} />
                            <ZhBaseItem label="专员 ID" value={product.handlerId ?? "-"} />
                            <ZhBaseItem label="专员联系电话" value={product.handlerMobile?.join(",") || "-"} />
                            <ZhBaseItem label="规格" value={product.productTypeSpec || "-"} />
                            <ZhBaseItem label="商品介绍" span={4} value={product.introduce || "-"} />
                            <ZhBaseItem label="结算说明" span={4} value={product.settlementDescn || "-"} />
                            <ZhBaseItem label="服务保障" span={4} value={product.serviceGuarantee || "-"} />
                            <ZhBaseItem label="寄售规则" span={4} value={product.regulation || "-"} />
                            {product.publishState === 4 ? (
                                <ZhBaseItem label="驳回原因" span={4} value={product.auditDescn || "-"} />
                            ) : null}
                        </ZhGrid>
                    </ZhMultiGrid>
                ) : null}
                {isAuditPage && auditState != null ? (
                    <div className="mt-4">
                        <Input.TextArea
                            aria-label={auditState ? "审核意见" : "驳回原因"}
                            placeholder={auditState ? "请输入审核意见" : "请输入驳回原因"}
                            rows={4}
                            value={auditDescn}
                            onChange={event => setAuditDescn(event.target.value)}
                        />
                        <Button
                            aria-label="提交审核"
                            className="mt-3"
                            loading={auditSubmitting}
                            type="primary"
                            onClick={() => void handleAudit()}
                        >
                            提交审核
                        </Button>
                    </div>
                ) : null}
                <Space className="mt-4" wrap>
                    {isAuditPage ? (
                        <>
                            <Button aria-label="同意" onClick={() => setAuditState(true)}>
                                同意
                            </Button>
                            <Button aria-label="拒绝" onClick={() => setAuditState(false)}>
                                拒绝
                            </Button>
                        </>
                    ) : detail ? (
                        <>
                            {canManage && product?.publishState === 1 ? (
                                <Button
                                    aria-label="审核寄售商品"
                                    onClick={() => void navigate(`/productManagement/consignment/audit?id=${id || ""}`)}
                                >
                                    审核
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 2 && [2, 4, 5].includes(product.publishState || 0) ? (
                                <Button
                                    aria-label="编辑寄售商品"
                                    onClick={() => void navigate(`/productManagement/consignment/edit?id=${id || ""}`)}
                                >
                                    编辑
                                </Button>
                            ) : null}
                            {canClosePermission &&
                            product?.state === 2 &&
                            [1, 2, 4, 5].includes(product.publishState || 0) ? (
                                <Button aria-label="关闭寄售商品" onClick={() => void handleClose()}>
                                    关闭
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 1 && [3, 4].includes(product.publishState || 0) ? (
                                <Button aria-label="下架寄售商品" onClick={() => void handleDownShelf()}>
                                    下架
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                    <Button onClick={() => void navigate("/productManagement/consignment")}>返回</Button>
                </Space>
            </>
        </section>
    );
}

export default ConsignmentDetailPage;
